import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppProvider, useAppContext } from './contexts/ThemeContext';
import Unauthorized from './components/Auth/Unauthorized';
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import MenuEditor from './components/Menu/MenuEditor';
import Header from './components/Layouts/Header';
import Sidebar from './components/Layouts/Sidebar';
import './index.css';
import TruckInfoEditor from './components/Info/TruckInfoEditor';
import Customers from './components/Customers/Customers';
import Orders from './components/Orders/Orders';
import LoyaltyProgramEditor from './components/Loyalty/LoyaltyProgramEditor';
import Notifications from './components/Notifications/Notifications';
import MenuItemCategoryEditor from './components/Menu/MenuItemCategoryEditor';
import { FirebaseProvider } from './components/Auth/FirebaseProvider';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import OrderTimeline from './components/Timeline/OrderTimeline';
import TruckHoursEditor from './components/Info/TruckHoursEditor';
import TruckSlotsEditor from './components/Info/TruckSlotsEditor';
import IngredientEditor from './components/Ingredient/IngredientEditor';
import PromoCodeManagement from './components/PromoCode/PromoCodeManagement';
import Loader from './components/Common/Loader';
import BundleEditor from './components/Bundles/BundleEditor';
import AccountingView from './components/Accounting/AccountingView';
import SalesStatsPage from './components/SalesStats/SalesStatsPage';

const PrivateLayout = ({ children }) => {
  const { drawerOpen } = useAppContext();
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Header />
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Sidebar />
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: 3, 
            overflow: 'auto',
            bgcolor: 'background.default',
            marginLeft: drawerOpen ? 0 : '-260px',
            transition: 'margin-left 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
            width: `calc(100% - ${drawerOpen ? 260 : 0}px)`
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

const PublicLayout = ({ children }) => {
  return (
    <div className="h-screen">
      {children}
    </div>
  );
};

const AdminRoute = ({ children }) => {
  const { currentUser, isAdmin, loading } = useAuth();

  if (loading) return <Loader/>;

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin) {
    return <Navigate to="/unauthorized" />;
  }

  return <PrivateLayout>{children}</PrivateLayout>;
};

const PublicRoute = ({ children }) => {
  const { currentUser, isAdmin, loading } = useAuth();

  if (loading) return <Loader/>;

  if (currentUser && isAdmin) {
    return <Navigate to="/" />;
  }

  return <PublicLayout>{children}</PublicLayout>;
};

function AppContent() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/unauthorized" element={<PublicRoute><Unauthorized /></PublicRoute>} />
        <Route path="/" element={<AdminRoute><Dashboard /></AdminRoute>} />
        <Route path="/orders" element={<AdminRoute><Orders /></AdminRoute>} />
        <Route path="/ordersTimeline" element={<AdminRoute><OrderTimeline /></AdminRoute>} />
        <Route path="/menu/categories" element={<AdminRoute><MenuItemCategoryEditor /></AdminRoute>} />
        <Route path="/menu/products" element={<AdminRoute><MenuEditor /></AdminRoute>} />
        <Route path="/menu/ingredients" element={<AdminRoute><IngredientEditor /></AdminRoute>} />
        <Route path="/menu/bundles" element={<AdminRoute><BundleEditor /></AdminRoute>} />
        <Route path="/truck/info" element={<AdminRoute><TruckInfoEditor /></AdminRoute>} />
        <Route path="/truck/hours" element={<AdminRoute><TruckHoursEditor /></AdminRoute>} />
        <Route path="/truck/slots" element={<AdminRoute><TruckSlotsEditor /></AdminRoute>} />
        <Route path="/loyalty" element={<AdminRoute><LoyaltyProgramEditor /></AdminRoute>} />
        <Route path="/customers" element={<AdminRoute><Customers /></AdminRoute>} />
        <Route path="/promo-codes" element={<AdminRoute><PromoCodeManagement /></AdminRoute>} />
        <Route path="/notifications" element={<AdminRoute><Notifications /></AdminRoute>} />
        <Route path="/sales" element={<AdminRoute><SalesStatsPage /></AdminRoute>} />
        <Route path="/accounting" element={<AdminRoute><AccountingView /></AdminRoute>} />
      </Routes>
    </Router>
  );
}

function MyApp() {
  return (
    <AppProvider>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
        <AuthProvider>
          <FirebaseProvider>
            <AppContent />
          </FirebaseProvider>
        </AuthProvider>
      </LocalizationProvider>
    </AppProvider>
  );
}

export default MyApp;